import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  ALEN_PRODUCT_TYPE_MAP,
  WRITABLE_PRODUCT_TYPES,
} from "src/services/alen/utils";
import UISelect, { Item } from "src/components/UI/Select";
import { useAlenSchedulingContext } from "../AlenSchedulingContext";
import {
  convertToScheduleTargetLevel,
  DeviceGroup,
  ScheduleTargetLevel,
  ScheduleTargetSelection,
} from "../types";
import DeviceSelection, {
  SCHEDULING_TARGET_LEVELS,
  SelectedDeviceList,
} from "./DeviceSelection";

function ScheduleTargetSelectionDialog({
  open,
  onClose,
  onNext,
}: {
  open: boolean;
  onClose: () => void;
  onNext: (target: ScheduleTargetSelection) => void;
}) {
  const handleClose = (
    _event: {},
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason && reason === "backdropClick") return;
    onClose();
  };

  const { alenDevices, newScheduling } = useAlenSchedulingContext();
  const getTargetValueFromDeviceGroup = (
    level: ScheduleTargetLevel,
    deviceGroups: DeviceGroup[]
  ) => {
    switch (level) {
      case ScheduleTargetLevel.PROPERTY:
        return [];
      case ScheduleTargetLevel.DEVICE_TYPE:
        return deviceGroups.map((group) => group.deviceGroupName);
      case ScheduleTargetLevel.DEVICE:
        return deviceGroups
          .map((group) =>
            group.deviceDetails
              .map((deviceDetail) => deviceDetail.deviceIds)
              .flat()
          )
          .flat();
      default:
        return [];
    }
  };
  const [selectedTargets, setSelectedTargets] = useState<
    ScheduleTargetSelection | undefined
  >();

  useEffect(() => {
    if (newScheduling.deviceGroupType) {
      setSelectedTargets({
        level: convertToScheduleTargetLevel(newScheduling.deviceGroupType),
        value: getTargetValueFromDeviceGroup(
          convertToScheduleTargetLevel(newScheduling.deviceGroupType),
          newScheduling.deviceGroups
        ),
      });
    } else {
      setSelectedTargets(undefined);
    }
  }, [newScheduling.deviceGroupType, newScheduling.deviceGroups]);

  const handleDevicesToggle = (deviceIds: string[]) => {
    setSelectedTargets({
      level: ScheduleTargetLevel.DEVICE,
      value: deviceIds,
    });
    if (deviceIds.length === 0) {
      setSelectedTargets(undefined);
    }
  };

  const handleNext = () => {
    if (!selectedTargets) return;
    onNext(selectedTargets);
  };

  const getDeviceCountByType = (type: string) =>
    alenDevices.filter(
      (device) => ALEN_PRODUCT_TYPE_MAP[device.product_id] === type
    ).length;

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
      <DialogTitle>Schedule Target</DialogTitle>
      <DialogContent
        sx={{ maxHeight: "80vh", overflowY: "scroll", gap: "1rem" }}
      >
        <Stack gap="1rem" paddingTop="1rem">
          <Stack direction="row" gap="1rem">
            <UISelect
              label="Select Devices"
              sx={{ width: "200px" }}
              value={selectedTargets?.level || ""}
              onChange={(value) =>
                setSelectedTargets({
                  level: value as ScheduleTargetLevel,
                  value: [],
                })
              }
            >
              {SCHEDULING_TARGET_LEVELS.map((level) => (
                <Item key={level.value} value={level.value}>
                  {level.label}
                </Item>
              ))}
            </UISelect>
            {selectedTargets?.level === ScheduleTargetLevel.DEVICE_TYPE && (
              <UISelect
                label="Device Model"
                multiple
                sx={{ width: "200px" }}
                value={selectedTargets.value}
                onChange={(value) => {
                  setSelectedTargets({
                    level: ScheduleTargetLevel.DEVICE_TYPE,
                    value,
                  });
                }}
              >
                {WRITABLE_PRODUCT_TYPES.filter(
                  (type) => getDeviceCountByType(type) > 0
                ).map((type) => (
                  <Item showCheckbox key={type} value={type}>
                    {`${type} (${getDeviceCountByType(type)})`}
                  </Item>
                ))}
              </UISelect>
            )}
            {selectedTargets?.level === ScheduleTargetLevel.DEVICE && (
              <DeviceSelection
                options={alenDevices.filter((device) =>
                  WRITABLE_PRODUCT_TYPES.includes(
                    ALEN_PRODUCT_TYPE_MAP[device.product_id]
                  )
                )}
                onChange={handleDevicesToggle}
                selected={selectedTargets.value}
              />
            )}
          </Stack>
          {selectedTargets?.level === ScheduleTargetLevel.DEVICE && (
            <SelectedDeviceList
              onDelete={(deviceId) =>
                setSelectedTargets((prev) => {
                  if (prev?.level !== ScheduleTargetLevel.DEVICE) return prev;
                  return {
                    level: ScheduleTargetLevel.DEVICE,
                    value: prev.value.filter((id) => id !== deviceId),
                  };
                })
              }
              selected={alenDevices.filter((device) =>
                selectedTargets.value.includes(device.id)
              )}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <>
          <Button onClick={onClose} color="danger">
            Cancel
          </Button>
          <Button
            onClick={handleNext}
            disabled={selectedTargets === undefined}
            color="primary"
          >
            Next
          </Button>
        </>
      </DialogActions>
    </Dialog>
  );
}

export default ScheduleTargetSelectionDialog;
